import type { EditableFloorPlan, EditorFeature } from '../models/editor-feature';
import { editableFeaturesOf, selectionKeyOf, type SelectionKey } from './selection';

/**
 * Finding a feature by what is written on it.
 *
 * A real floor holds several hundred features, most of them visually identical rectangles,
 * and the questions an admin actually arrives with are "where is workplace
 * 3f2c…?" — from a support ticket — and "which of these is the one labelled Kitchen". Neither
 * is answerable by looking: the id is not drawn anywhere, and forty desks look the same.
 *
 * Searches the stored identifiers as well as the editor's own, because the id in a ticket or
 * a database row is the stored one, and the editor renames duplicates on load.
 */

export type FindFieldKey =
  | 'floorplan-find-field-text'
  | 'floorplan-find-field-icon'
  | 'floorplan-find-field-id'
  | 'floorplan-find-field-element-type'
  | 'floorplan-find-field-room'
  | 'floorplan-find-field-pod'
  | 'floorplan-find-field-property';

export interface FindMatch {
  readonly key: SelectionKey;
  readonly feature: EditorFeature;
  /** Which property matched, so a result that looks unrelated explains itself. */
  readonly fieldKey: FindFieldKey;
  /** The matching value, verbatim. */
  readonly value: string;
}

/** A field of a feature worth searching, paired with what to call it in the results. */
interface Candidate {
  readonly fieldKey: FindFieldKey;
  readonly value: string;
}

/**
 * How many results are worth returning.
 *
 * A one-letter query matches most of the plan, and a list of six hundred rows is neither
 * useful nor cheap to render. The panel says how many were left out rather than pretending
 * the list is complete.
 */
export const FIND_LIMIT = 50;

const candidatesOf = (feature: EditorFeature): Candidate[] => {
  const candidates: Candidate[] = [
    { fieldKey: 'floorplan-find-field-id', value: feature.featureId },
    { fieldKey: 'floorplan-find-field-id', value: feature.storedId },
  ];

  if (feature.kind === 'textLabel') {
    candidates.push({ fieldKey: 'floorplan-find-field-text', value: feature.text });
  }

  if (feature.kind === 'iconLabel' && feature.iconName) {
    candidates.push({ fieldKey: 'floorplan-find-field-icon', value: feature.iconName });
  }

  if (feature.kind === 'polygon' && feature.elementType) {
    candidates.push({ fieldKey: 'floorplan-find-field-element-type', value: feature.elementType });
  }

  if (feature.kind === 'desk') {
    if (feature.roomId) {
      candidates.push({ fieldKey: 'floorplan-find-field-room', value: feature.roomId });
    }

    if (feature.podId) {
      candidates.push({ fieldKey: 'floorplan-find-field-pod', value: feature.podId });
    }
  }

  // Everything the editor does not model is searchable too. This is where a drawing's own
  // names live — `name`, `Naam`, `roomNumber` — and they are exactly what somebody reading a
  // floor plan on paper would type in. Only scalars: an object would stringify to
  // "[object Object]", which matches nothing anybody meant.
  Object.entries(feature.unknownProperties).forEach(([name, value]) => {
    if (typeof value === 'string' || typeof value === 'number') {
      candidates.push({ fieldKey: 'floorplan-find-field-property', value: `${name}: ${value}` });
    }
  });

  return candidates.filter((candidate) => candidate.value.length > 0);
};

/**
 * Where a query matched, or -1.
 *
 * Case-insensitive, and returning the position rather than a boolean so the results can be
 * ordered by how well they match: a desk whose id *starts* with what was typed is a better
 * answer than a room whose description happens to contain it.
 */
const matchIndexOf = (value: string, query: string): number => value.toLowerCase().indexOf(query);

export const findFeatures = (
  floorPlan: EditableFloorPlan | undefined,
  query: string,
  limit: number = FIND_LIMIT,
): FindMatch[] => {
  const needle = query.trim().toLowerCase();

  if (!floorPlan || needle.length === 0) {
    return [];
  }

  const scored: Array<{ match: FindMatch; rank: number }> = [];

  editableFeaturesOf(floorPlan).forEach((feature) => {
    let best: { candidate: Candidate; rank: number } | undefined;

    candidatesOf(feature).forEach((candidate) => {
      const index = matchIndexOf(candidate.value, needle);

      if (index < 0) {
        return;
      }

      // Exact, then from the start, then anywhere. A stored id pasted from a ticket is an
      // exact match and belongs at the top even on a plan where it also appears inside
      // twenty other properties.
      const fromStart = index === 0 ? 1 : 2;
      const rank = candidate.value.toLowerCase() === needle ? 0 : fromStart;

      if (!best || rank < best.rank) {
        best = { candidate, rank };
      }
    });

    if (best) {
      scored.push({
        match: {
          key: selectionKeyOf(feature),
          feature,
          fieldKey: best.candidate.fieldKey,
          value: best.candidate.value,
        },
        rank: best.rank,
      });
    }
  });

  // A stable sort, so features of equal rank stay in painting order rather than shuffling
  // between keystrokes as the admin types.
  return scored
    .map((entry, index) => ({ ...entry, index }))
    .sort((first, second) => first.rank - second.rank || first.index - second.index)
    .slice(0, limit)
    .map((entry) => entry.match);
};

/** How many features matched in total, for saying what the capped list left out. */
export const countMatches = (
  floorPlan: EditableFloorPlan | undefined,
  query: string,
): number => findFeatures(floorPlan, query, Number.MAX_SAFE_INTEGER).length;
